import { DrawerDialog, DrawerDialogProps } from '@/common/components/drawer-dialog';
import Spacer from '@/common/components/spacer';
import { Link, SubmitFunction, useSubmit } from 'react-router';
import { CreditScore, VehicleSavingsCalculatorV2 } from '@/catalog/types';
import { useState, useEffect, useCallback, useRef } from 'react';
import { CreditRange } from '../../constants/savings-calculator';
import StatusBanner from '@/common/components/status-banner';
import { fullstoryEvent } from '@/fullstory/utils/fullstory-event';
import { fullstoryCalculatorClosedEvent } from '@/fullstory/utils/fullstory-calculator-closed-event';
import MissingImagePlaceholder from '../missing-image-placeholder';
import VehicleSubDetails from '../vehicle/vehicle-sub-details';
import SavingsCalculatorBlobContainer from './savings-calculator-blob-container';
import { Button } from '@/shadcn-ui/button';
import { scrollToRef } from '@/common/utils/scroll-to-ref';
import SavingsMathSection from './savings-math-section';
import { LoaderButton } from '@/common/components/loader-button';
import SavingsInputForm from './savings-input-form';
import { getDefaultPlanMileage } from '@/catalog/utils/get-default-plan-mileage';
import SavingsSourcesSection from './savings-sources-section';
import { AgeRange } from '@/catalog/types/age-range';
import webpWithPngFallback from '@/common/utils/webp-with-png-fallback';
import SavingsCostComparison from './savings-cost-comparison';
import { MileageOption } from '@/catalog/utils/get-miles-per-year-options';
import { useDisableSpaNavigation } from '@/util-react-router/hooks/use-disable-spa-navigation';

export interface SavingsCalculatorV2DrawerDialogProps extends DrawerDialogProps {
  vehicleId: number;
  imageUrl?: string;
  modelYear: number;
  make: string;
  model: string;
  trim: string;
  odometer: number;
  savings: VehicleSavingsCalculatorV2;
  openedFrom: 'inventory' | 'pdp' | 'review_and_pay';
  creditScores: CreditScore[];
  ageRanges?: AgeRange[];
  // Session values
  selectedPlanMileage?: number;
  selectedCreditScoreId?: string;
  selectedOwnershipYears?: number;
  selectedAge?: boolean;
  selectedAgeRangeId?: string;
  // Review and Pay specific props
  disableCreditScore?: boolean;
  disableAge?: boolean;
  // Form validation props
  error?: string;
  /** Function to submit the form data */
  submit: SubmitFunction;
  /** * Intent for form data submission. Defaults to 'add_savings_calculator_inputs' */
  submitIntent?: string;
  /** If true, the drawer will navigate to PDP when the user clicks the "View car details" button */
  navigateOnViewCarDetails?: boolean;
  membershipCost?: string;
  marketId?: string;
  /** Flag indicating if user selected a new mileage plan from inventory filters, on PDP, or on Review & Pay  */
  hasChangedSelectedPlan: boolean;
  hasChangedSelectedPlanDelegate: () => void;
  /** Cross Market data for shipping fee tooltip */
  crossMarket?: {
    originMarketName: string;
    destinationMarketName: string;
  };
  /** CDN URL */
  cdnUrl: string;
  milesPerYearOptions: MileageOption[];
}

export default function SavingsCalculatorV2DrawerDialog({
  open,
  handleClose,
  creditScores,
  ageRanges,
  selectedPlanMileage = 850,
  selectedOwnershipYears = 5,
  selectedCreditScoreId = 'excellent',
  selectedAge = true,
  selectedAgeRangeId = '45_plus',
  disableCreditScore = false,
  disableAge = false,
  vehicleId,
  imageUrl,
  modelYear,
  make,
  model,
  trim,
  odometer,
  savings,
  openedFrom,
  error,
  submit: savingsCalculatorSubmit,
  submitIntent = 'add_savings_calculator_inputs',
  navigateOnViewCarDetails = false,
  marketId = '',
  membershipCost,
  hasChangedSelectedPlan,
  hasChangedSelectedPlanDelegate,
  crossMarket,
  cdnUrl,
  milesPerYearOptions,
}: SavingsCalculatorV2DrawerDialogProps) {
  const [planMileage, setPlanMileage] = useState<number>(
    getDefaultPlanMileage(selectedPlanMileage)
  );

  // credit range's enum value corresponds to the risk credit score id in ruby
  const [creditScore, setCreditScore] = useState<CreditRange>(
    selectedCreditScoreId as CreditRange
  );

  const [ownershipYears, setOwnershipYears] = useState<number>(selectedOwnershipYears);

  const [atOrOver25, setAtOrOver25] = useState<boolean>(selectedAge);
  const [ageRangeId, setAgeRangeId] = useState(selectedAgeRangeId);

  const [minDisplayedValue, setMinDisplayedValue] = useState<number | undefined>();
  const [maxDisplayedValue, setMaxDisplayedValue] = useState<number | undefined>();
  const [hasChangedAge, setHasChangedAge] = useState(false);
  const [hasChangedCredit, setHasChangedCredit] = useState(false);
  const [hasChangedMiles, setHasChangedMiles] = useState(false);
  const [hasChangedOwnershipYears, setHasChangedOwnershipYears] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  const topRef = useRef(null);
  const submit = useSubmit();

  const handleCloseAndSyncSessionCart = () => {
    // Only try to re-sync age, credit, and plan when they differ from what's in the session cart
    if (
      atOrOver25 !== selectedAge ||
      ageRangeId !== selectedAgeRangeId ||
      creditScore !== selectedCreditScoreId ||
      planMileage !== selectedPlanMileage
    ) {
      submit(
        {
          intent: 'update_plan_mileage_credit_and_age',
          plan_mileage: planMileage,
          credit: creditScore,
          over25: atOrOver25,
          ageRangeId: ageRangeId,
        },
        { method: 'post' }
      );
    }

    handleClose();
  };

  const handleScrollToTop = () => {
    scrollToRef(topRef, 'top', undefined, true);
  };

  const handleCreditScoreChange = (event: CreditRange) => {
    savingsCalculatorSubmit(
      {
        intent: submitIntent,
        creditScore: event,
        atOrOver25: atOrOver25,
        ageRangeId: ageRangeId,
        milesDrivenPerMonth: planMileage,
        ownershipYears: ownershipYears,
        vehicleId: vehicleId,
        marketId: marketId,
      },
      { method: 'post' }
    );
    setCreditScore(event);
    setHasChangedCredit(true);
  };

  const handleAgeChange = (checked: boolean) => {
    savingsCalculatorSubmit(
      {
        intent: submitIntent,
        creditScore: creditScore,
        atOrOver25: checked,
        ageRangeId: ageRangeId,
        milesDrivenPerMonth: planMileage,
        ownershipYears: ownershipYears,
        vehicleId: vehicleId,
        marketId: marketId,
      },
      { method: 'post' }
    );
    setAtOrOver25(checked);
    setHasChangedAge(true);
  };

  const handleAgeRangeChange = (id: string) => {
    savingsCalculatorSubmit(
      {
        intent: submitIntent,
        creditScore: creditScore,
        atOrOver25: atOrOver25,
        ageRangeId: id,
        milesDrivenPerMonth: planMileage,
        ownershipYears: ownershipYears,
        vehicleId: vehicleId,
        marketId: marketId,
      },
      { method: 'post' }
    );
    setAgeRangeId(id);
    setHasChangedAge(true);
  };

  const handlePlanMileageChange = useCallback(
    (event: string) => {
      const mileage = parseInt(event);
      savingsCalculatorSubmit(
        {
          intent: submitIntent,
          creditScore: creditScore,
          atOrOver25: atOrOver25,
          ageRangeId: ageRangeId,
          milesDrivenPerMonth: mileage,
          ownershipYears: ownershipYears,
          vehicleId: vehicleId,
          marketId: marketId,
        },
        { method: 'post' }
      );
      setPlanMileage(mileage);
      setHasChangedMiles(true);
    },
    [
      ageRangeId,
      atOrOver25,
      creditScore,
      marketId,
      ownershipYears,
      savingsCalculatorSubmit,
      submitIntent,
      vehicleId,
    ]
  );

  const handleOwnershipYearsChange = (event: string) => {
    const years = parseInt(event);

    savingsCalculatorSubmit(
      {
        intent: submitIntent,
        creditScore: creditScore,
        atOrOver25: atOrOver25,
        ageRangeId: ageRangeId,
        milesDrivenPerMonth: planMileage,
        ownershipYears: years,
        vehicleId: vehicleId,
        marketId: marketId,
      },
      { method: 'post' }
    );
    setOwnershipYears(years);
    setHasChangedOwnershipYears(true);
  };

  const clearInteractions = () => {
    setMinDisplayedValue(undefined);
    setMaxDisplayedValue(undefined);
    setHasChangedAge(false);
    setHasChangedCredit(false);
    setHasChangedMiles(false);
    setHasChangedOwnershipYears(false);
    setHasBeenOpened(false);
  };

  const handleTotalSavingsChanged = useCallback(
    (value: number | undefined) => {
      if (value === undefined) {
        return;
      }
      if (minDisplayedValue === undefined || value < minDisplayedValue) {
        setMinDisplayedValue(value);
      }
      if (maxDisplayedValue === undefined || value > maxDisplayedValue) {
        setMaxDisplayedValue(value);
      }
    },
    [minDisplayedValue, maxDisplayedValue]
  );

  useEffect(() => {
    if (open) {
      fullstoryEvent('savings_calculator_opened', {
        engagement_location: openedFrom,
      });
      setHasBeenOpened(true);
    }
  }, [open, openedFrom]);

  useEffect(() => {
    if (!open && hasBeenOpened) {
      fullstoryCalculatorClosedEvent({
        engagement_location: openedFrom,
        changed_age: hasChangedAge,
        changed_credit: hasChangedCredit,
        changed_miles: hasChangedMiles,
        changed_months: hasChangedOwnershipYears,
        min_savings: minDisplayedValue,
        max_savings: maxDisplayedValue,
      });
      clearInteractions();
    }
  }, [
    open,
    hasBeenOpened,
    hasChangedAge,
    hasChangedCredit,
    hasChangedMiles,
    minDisplayedValue,
    maxDisplayedValue,
    hasChangedOwnershipYears,
    openedFrom,
  ]);

  useEffect(() => {
    if (open && savings) {
      handleTotalSavingsChanged(savings.flexcar_savings_over_ownership_year_term);
    }
  }, [open, savings, handleTotalSavingsChanged]);

  useEffect(() => {
    /**
     * When the drawer is opened again after a user selects a new mileage plan, reset the default plan milage
     * to the default plan mileage dictated by the plan mileage stored in the session cart.
     */
    if (hasBeenOpened && hasChangedSelectedPlan) {
      handlePlanMileageChange(String(getDefaultPlanMileage(selectedPlanMileage)));
      hasChangedSelectedPlanDelegate();
    }
  }, [
    handlePlanMileageChange,
    hasBeenOpened,
    hasChangedSelectedPlan,
    hasChangedSelectedPlanDelegate,
    selectedPlanMileage,
  ]);

  const { disableSpaNavigation } = useDisableSpaNavigation();

  return (
    <DrawerDialog
      open={open}
      handleClose={handleCloseAndSyncSessionCart}
      title="FLEXCAR SAVINGS CALCULATOR™"
      variant="center-title"
      headerClassName="bg-flexcar-neutral100/90 backdrop-blur-[10px] rounded-t-2xl"
      childrenContainerClassName="bg-flexcar-neutral90 p-0 md:rounded-b-2xl"
    >
      {error ? (
        <>
          <StatusBanner
            variant="error"
            message={{ text: error }}
            shouldScrollToTop={true}
          />
          <Spacer variant="height16" />
        </>
      ) : null}
      {savings ? (
        <>
          <div
            className="bg-[image:var(--background-svg)] bg-cover bg-center bg-no-repeat px-[16px] pb-[24px]"
            style={
              {
                '--background-svg': `url(${webpWithPngFallback(`${cdnUrl}/web/images/savings-calculator-gradient-background.webp`)})`,
              } as React.CSSProperties
            }
            ref={topRef}
          >
            <p className="body2 flex items-center justify-center pb-3 pt-2 text-center">
              See how much you&apos;ll save with Flexcar vs. buying an identical used car.
            </p>
            <SavingsCalculatorBlobContainer className="flex-row items-center gap-[16px] p-[16px]">
              {imageUrl ? (
                <div className="h-full w-[112px] overflow-hidden rounded-[16px]">
                  <img
                    src={imageUrl}
                    alt={`${modelYear} ${make} ${model}`}
                    className="scale-118"
                  />
                </div>
              ) : (
                <div className="h-full w-[112px]">
                  <MissingImagePlaceholder />
                </div>
              )}
              <div>
                <p className="body-strong">
                  {modelYear} {make} {model}
                </p>
                <Spacer variant="height4" />
                <VehicleSubDetails trim={trim} odometer={odometer} />
              </div>
            </SavingsCalculatorBlobContainer>

            <Spacer variant="height12" />

            <SavingsInputForm
              atOrOver25={atOrOver25}
              handleAgeChangeDelegate={handleAgeChange}
              disableAge={disableAge}
              creditScore={creditScore}
              ageRanges={ageRanges}
              ageRangeId={ageRangeId}
              handleAgeRangeChangeDelegate={handleAgeRangeChange}
              handleCreditScoreChangeDelegate={handleCreditScoreChange}
              disableCreditScore={disableCreditScore}
              creditScores={creditScores}
              ownershipYears={ownershipYears}
              handleOwnershipYearsChangeDelegate={handleOwnershipYearsChange}
              planMileage={planMileage}
              handlePlanMileageChangeDelegate={handlePlanMileageChange}
              milesPerYearOptions={milesPerYearOptions}
            />

            <Spacer variant="height12" />

            <SavingsCostComparison
              savings={savings}
              ownershipYears={ownershipYears}
              membershipCost={membershipCost}
            />

            <Spacer variant="height12" />

            <SavingsMathSection
              savings={savings}
              ownershipYears={ownershipYears}
              planMileage={planMileage}
              originMarketName={crossMarket?.originMarketName}
              destinationMarketName={crossMarket?.destinationMarketName}
            />

            <Spacer variant="height12" />

            <SavingsSourcesSection />
          </div>
        </>
      ) : null}
      <div className="flex flex-col justify-center px-[16px]">
        {navigateOnViewCarDetails ? (
          <Link
            to={`/inventory/${marketId}/${vehicleId}`}
            className="w-full"
            reloadDocument={disableSpaNavigation}
          >
            <LoaderButton variant="primary" className="w-full">
              View car details
            </LoaderButton>
          </Link>
        ) : (
          <Button variant="primary" onClick={handleClose} className="w-full">
            View car details
          </Button>
        )}
        <Spacer variant="height24" />
        <Button variant="tertiary" onClick={handleScrollToTop} className="w-full">
          Back to top
        </Button>
      </div>
      <Spacer variant="height24" />
      <p className="body3 px-[16px] text-center text-flexcar-neutral50">
        The Flexcar Savings Calculator provides estimates based on proprietary formulas
        and third-party data sources. These estimates are for informational purposes only
        and may vary based on individual circumstances. Estimated savings are not
        guaranteed and may not apply to all members. The final cost, including any
        applicable savings, will be determined at checkout and is subject to member
        details, vehicle selection, mileage, and other factors.
      </p>
      <Spacer variant="height24" />
    </DrawerDialog>
  );
}
